import { Selection } from "@tiptap/pm/state";

import { hasDecorator } from "@/components/expression/utils/decorators/utils";
import {
  createMatrixFrom,
  canConvertToMatrix,
  isMatrixCell,
  insertRow,
  isActiveMatrixCell,
  hasGroupBounds,
  canConvertToInline,
  replaceMatrixWithCellAtPosition,
} from "@/components/expression/utils/matrix";
import {
  selectColumn,
  deleteMatrixSelection,
  selectRow,
  preserveSelectionCursorPosition,
} from "@/components/expression/utils/matrixSelection";
import { addGraphExpression, removeGraphExpression } from "@/components/graph/utils";
import { commitPendingInput } from "@/components/input/utils/commitPendingInput";
import { splitInlineExpression } from "@/components/input/utils/splitInlineExpression";
import { splitInlineExpressionAndCreateNewLine } from "@/components/input/utils/splitInlineExpressionAndCreateNewLine";
import { splitInlineExpressionAndCreateTextBlock } from "@/components/input/utils/splitInlineExpressionAndCreateTextBlock";
import { setNodeDecorators } from "@/components/suggestions/utils";
import { openBlockControls } from "@/components/tiptap/block-navigation/BlockNavigation";
import {
  nodeIsExpression,
  postProcessNodeRemoval,
} from "@/components/tiptap/tiptap-expression/utils";
import { duplicateSelection } from "@/components/tiptap/utils/duplicateTiptapLine";
import { triggerKeyboardInput } from "@/input/handleKeyboardInput";
import { type KeyboardInputAction } from "@/input/types";
import { deleteNode, deleteSelectedNodes } from "@/input/utils/editOperators";
import { enterAdjacentLabelAndDelete, shouldRemoveLabels } from "@/input/utils/labels";
import {
  reopenGroupLeft,
  reopenGroupRight,
  shouldMoveOutOfOpenGroupLeft,
  shouldMoveOutOfOpenGroupRight,
  shouldMoveToOpenGroupLeft,
  shouldMoveToOpenGroupRight,
  shouldReopenGroupLeft,
  shouldReopenGroupRight,
  shouldUngroup,
  ungroup,
} from "@/input/utils/openGroup";
import { cursorIsActive } from "@/models/CursorModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GraphExpressionNodeModel } from "@/models/nodes/GraphExpressionNodeModel";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { isGroupSymbol } from "@/models/nodes/groups/utils";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { UnitNodeModel } from "@/models/nodes/UnitNodeModel";
import { hasUnit } from "@/models/nodes/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isSupSubNode } from "@/models/utils/semanticChecks";
import { HOTKEY_MAP } from "@/utils/hotkeys";

import type { DocumentModel } from "@/models/DocumentModel";
import type { SupSubOperator } from "@/models/nodes/operators/types";
import type { NodeModel } from "@/models/nodes/types";
import type { KeyCombination, Symbol } from "@corca/data";

function selectNodeBeforeRemoving(node?: NodeModel): node is Maybe<NodeModel> {
  return !!(
    node &&
    ((node.is(FunctionNodeModel) && !node.paramsAreEmpty) ||
      (node.is(VariableNodeModel) && !node.paramsAreEmpty) ||
      (node.is(OperatorNodeModel) &&
        (node.arity !== "unary" ||
          node.placement === "function" ||
          (node.placement === "postfix" && !node.paramsAreEmpty))))
  );
}

function selectParentNodeBeforeRemoving(node?: NodeModel): node is Maybe<NodeModel> {
  const { parent, index } = node || {};

  if (index === 0 && parent?.is(OperatorNodeModel)) {
    return parent.placement === "postfix" && !parent.paramsAreEmpty;
  }

  return !!(
    index === 1 &&
    ((parent?.is(FunctionNodeModel) && !parent.paramsAreEmpty) ||
      (parent?.is(VariableNodeModel) && !parent.paramsAreEmpty))
  );
}

function getMoveAfterNode(doc: DocumentModel): NodeModel | undefined {
  const { parent: cursorParent } = doc.cursor.node ?? {};

  if (cursorParent?.is(FunctionNodeModel) || cursorParent?.is(VariableNodeModel)) {
    return cursorParent;
  }

  if (cursorParent?.is(OperatorNodeModel) && !cursorParent.isNested) {
    if (cursorParent.parent?.is(FunctionNodeModel) || cursorParent.parent?.is(NumberNodeModel)) {
      return cursorParent.parent;
    }

    return cursorParent;
  }

  if (cursorParent?.is(UnitNodeModel)) {
    return cursorParent.parent;
  }

  return undefined;
}

function getSubSupOperator(node?: InlineNodeModel): SupSubOperator | undefined {
  if (!node) {
    return undefined;
  }

  const { doc, parent } = node;

  if (isSupSubNode(doc.cursor.nodeBeforeCursor)) {
    return doc.cursor.nodeBeforeCursor;
  }

  if (parent && "power" in parent) {
    return parent.power;
  }

  return undefined;
}

function shouldRemoveSubSupParam(node?: InlineNodeModel): boolean {
  if (!node) {
    return false;
  }

  const { parent, doc } = node;
  const { cursor } = doc;
  const operator = getSubSupOperator(node);

  if (!parent || !operator) {
    return false;
  }

  if (cursor.nodeBeforeCursor === operator) {
    return true;
  }

  if ("parameters" in parent) {
    return cursor.index === 0 && node === parent.parameters[0];
  }

  return cursor.index === 0;
}

function removeSubSupParam(node: SupSubOperator | undefined, event: KeyboardEvent) {
  if (!node) return;

  const { doc, parameters } = node;

  if (parameters[0].nodes.length > 1) {
    doc.cursor.moveTo(parameters[0], parameters[0].nodes.length);
    triggerKeyboardInput(doc, "Backspace", event);
  } else {
    const { index, parent } = node;

    doc.removeNode(node);

    if (parent?.is(InlineNodeModel)) {
      doc.cursor.moveTo(parent, index);
    }
  }
}

export const globalShortcuts = {
  [HOTKEY_MAP["block-controls"]]: {
    canPickSuggestion: () => false,
    action: (doc: DocumentModel) => openBlockControls(doc),
  },
  Escape: {
    action: (doc: DocumentModel) => {
      doc.selection.resetSelection();
      doc.editor.resetInput(true);
      doc.editor.suggestions.reset();
      return true;
    },
  },
  [HOTKEY_MAP["duplicate-line"]]: {
    canPickSuggestion: () => false,
    action: (doc: DocumentModel) => duplicateSelection(doc),
  },
  Enter: {
    canPickSuggestion: () => false,
    action: (doc: DocumentModel) => {
      const { tiptap } = doc.editor;

      if (!tiptap) return false;
      if (doc.editor.suggestions.activeSuggestion) return false;

      const { level } = doc.cursor;

      if (doc.selection.hasSelectedEditableNode) {
        doc.selection.editSelectedNode();
        return true;
      }

      if (doc.onSubmit) {
        commitPendingInput(doc);
        doc.onSubmit();
        return true;
      }

      if (!doc.editor.suggestions.activeSuggestion && doc.input.value.length > 0) {
        splitInlineExpression(doc);
        return true;
      }

      if (isActiveMatrixCell(doc.cursor)) {
        const { parent } = doc.cursor.node;

        if (!hasGroupBounds(parent.bounds)) {
          doc.cursor.moveRight();
          return true;
        }
      }

      if (level > 0) {
        doc.cursor.moveToLevel(level - 1);
        return true;
      }

      const moveAfterNode = getMoveAfterNode(doc);

      if (moveAfterNode) {
        doc.cursor.moveAfterNode(moveAfterNode);
        return true;
      }

      if (doc.editor.editorState.isTable) {
        if (tiptap.commands.goToNextCellVertical(1)) {
          tiptap.commands.focus();
        } else {
          tiptap.commands.appendParagraph({ forceFocus: true });
        }

        return true;
      }

      if (doc.editor.editorState.isExpression) {
        splitInlineExpressionAndCreateTextBlock(doc);
        return true;
      }

      return false;
    },
  },
  /**
   *  this is added for compatibility and could be excessive
   */
  "Shift+Enter": {
    canPickSuggestion: (doc) => !!doc.editor.suggestions.activeSuggestion,
    action: (doc: DocumentModel) => {
      const { node } = doc.cursor;
      const { parent } = node ?? {};
      const { level } = doc.cursor;

      if (doc.selection.hasSelectedEditableNode) {
        doc.selection.editSelectedNode();
        return true;
      }

      if (node && canConvertToMatrix(node)) {
        createMatrixFrom(node, { cols: 1, rows: 2 });
        return true;
      }

      if (node && isMatrixCell(node)) {
        insertRow(node);
        return true;
      }

      if (!doc.editor.suggestions.activeSuggestion && doc.input.value.length > 0) {
        splitInlineExpression(doc);
        return true;
      }

      if (level > 0) {
        doc.cursor.moveToLevel(level - 1);
        return true;
      }

      if (parent?.is(GraphExpressionNodeModel) && parent.parent?.is(GraphNodeModel)) {
        addGraphExpression(parent.parent, parent);
        return true;
      }

      if (doc.editor.tiptap) {
        if (doc.editor.editorState.isTable) {
          doc.editor.tiptap.chain().addRowAfter().goToNextCellVertical(1).focus().run();
          return true;
        }

        if (doc.editor.editorState.isExpression) {
          splitInlineExpressionAndCreateNewLine(doc);
          return true;
        }
      }

      return false;
    },
  },
  "Mod+Enter": {
    canPickSuggestion: () => false,
    action: (doc: DocumentModel): boolean => {
      const { tiptap } = doc.editor;
      if (!tiptap) return false;

      doc.editor.resetInput();
      doc.transact(() => {
        tiptap.commands.appendParagraph({ forceFocus: true });
      }, "shortcut:Meta+Enter");
      return true;
    },
  },
  "Mod+Shift+Enter": {
    canPickSuggestion: () => false,
    action: (doc: DocumentModel) => {
      const { tiptap } = doc.editor;
      if (!tiptap) return false;

      doc.editor.resetInput();
      doc.transact(() => {
        tiptap.commands.prependParagraph({ forceFocus: true });
      }, "shortcut:Meta+Enter");
      return true;
    },
  },
  "Alt+Enter": {
    action: (doc: DocumentModel): boolean => {
      const { isTable, isExpression } = doc.editor.editorState;

      if (isTable && isExpression) {
        splitInlineExpressionAndCreateNewLine(doc);
        return true;
      }

      const { tiptap } = doc.editor;
      if (!tiptap) return false;

      doc.editor.resetInput();
      doc.transact(() => {
        tiptap.commands.appendParagraph({ forceFocus: true });
      }, "shortcut:Alt+Enter");
      return true;
    },
  },
  Space: {
    canPickSuggestion: (doc: DocumentModel, input?: string) => {
      if (isGroupSymbol(input ?? "") || /^[.,]+$/.test(input ?? "")) {
        return false;
      }

      if (doc.editor.suggestions.activeSuggestion) {
        return true;
      }

      return false;
    },
    preventDefault: (doc: DocumentModel) => {
      const { level } = doc.cursor;
      return !doc.editor.suggestions.activeSuggestion || level > 0;
    },
    action: (doc: DocumentModel, { triggerMode }) => {
      if (triggerMode === "after-autosuggest" && !doc.cursor.isAtTheEndOfExpression) {
        return false;
      }

      const { level } = doc.cursor;

      if (level > 0) {
        doc.cursor.moveToLevel(level - 1);
        return false;
      }

      doc.input.setValue(`${doc.input.value} `);
      splitInlineExpression(doc);

      return true;
    },
  },
  Delete: {
    action: (doc: DocumentModel, { event, input }): boolean => {
      const { node, index, nodeAfterCursor } = doc.cursor;
      const { tiptap } = doc.editor;
      if (!tiptap) return false;

      if (input) {
        const { editingNode, inputSelection, value } = doc.input;
        if (
          !editingNode?.isInlineEditable() ||
          !inputSelection ||
          inputSelection.from < value.length ||
          inputSelection.from !== inputSelection.to
        ) {
          return false;
        }

        if (
          inputSelection.from === value.length &&
          inputSelection.to === value.length &&
          hasUnit(editingNode, "postfix")
        ) {
          const { inlineNode } = editingNode.unitNode;

          doc.editor.resetInput(true);
          doc.cursor.moveTo(inlineNode, 0);
          triggerKeyboardInput(doc, "Delete", event);
          return true;
        }
      }

      // Multiple node selected, call Backspace to remove them.
      if (doc.selection.hasSelectedNodes) {
        return globalShortcuts.Backspace.action(doc, { event, input });
      }
      // The first press breaks computed node's link instead of deleting,
      // leaving plain nodes a second press can edit.
      if (nodeAfterCursor && doc.computedNodes.unlinkComputedNode(nodeAfterCursor)) {
        return true;
      }
      // Select fractions, functions and other before removing
      if (selectNodeBeforeRemoving(nodeAfterCursor)) {
        doc.selection.selectNode(nodeAfterCursor);
        return true;
      }
      // We are in the end of the math block. Remove the next TipTap symbol.
      if (node?.parent?.is(WrapperNodeModel) && index === node.nodes.length) {
        const { state } = tiptap;
        const from = state.selection.to;
        const { node: originalNode, index: originalIndex } = doc.cursor;

        const selectionTo = Selection.findFrom(state.doc.resolve(from + 1), 1);
        if (!selectionTo) return false;
        const to = selectionTo.anchor;

        doc.transact(() => {
          tiptap.commands.deleteRange({ from, to });
        }, "shortcut:Delete");

        // If the next node after removed character is expression, we need to return node to the
        // original position. Run it with queueMicrotask to wait till tiptap onUpdate callback
        // is finished.
        queueMicrotask(() => {
          doc.cursor.moveTo(originalNode, originalIndex);
        });
        return true;
      }

      const nodeAfterEdit = doc.input.editingNode?.nextSiblings[0];

      if (nodeAfterEdit?.isInlineEditable()) {
        doc.transact(() => {
          let nodeValue = nodeAfterEdit.value;
          while (nodeValue.length > 0) {
            nodeValue = nodeValue.substring(1);
            if (nodeAfterEdit.validateValue(nodeValue)) break;
          }

          if (nodeValue.length > 0) {
            nodeAfterEdit.value = nodeValue;
          } else if (node && index) {
            deleteNode(node, index + 1);
            doc.selection.resetSelection();
            doc.editor.resetInput(true);
          }
          postProcessNodeRemoval(doc);
        }, "shortcut:delete");
        return true;
      }

      if (!doc.input.editingNode && nodeAfterCursor && hasUnit(nodeAfterCursor, "prefix")) {
        doc.cursor.moveRight();
        triggerKeyboardInput(doc, "Delete", event);
        return true;
      }

      if (!doc.input.editingNode && nodeAfterCursor?.isInlineEditable()) {
        const nodeAfter = nodeAfterCursor;

        let nodeValue = nodeAfter.value;
        while (nodeValue.length > 0) {
          nodeValue = nodeValue.substring(1);
          if (nodeAfter.validateValue(nodeValue)) break;
        }

        doc.transact(() => {
          if (nodeValue.length > 0) {
            nodeAfter.value = nodeValue;
          } else {
            deleteNode(nodeAfter);
            doc.selection.resetSelection();
            doc.editor.resetInput(true);
          }
          postProcessNodeRemoval(doc);
        }, "shortcut:delete");

        return true;
      }

      if (doc.input.editingNode?.isInlineEditable()) {
        doc.cursor.moveAfterNode(doc.input.editingNode);
        doc.editor.resetInput(true);
        doc.cursor.moveRight();
        doc.selection.resetSelection();
        globalShortcuts.Backspace.action(doc, { event, input: "" });
        return true;
      }

      if (shouldRemoveLabels(doc, "left")) {
        doc.transact(() => {
          enterAdjacentLabelAndDelete(doc, "left", event);
        }, "shortcut:removeLabels");

        return true;
      }

      if (shouldMoveToOpenGroupRight(doc) || shouldMoveOutOfOpenGroupLeft(doc)) {
        doc.cursor.moveRight();
        triggerKeyboardInput(doc, "Delete", event);
        return true;
      }

      if (shouldUngroup(doc, "right")) {
        doc.transact(() => {
          ungroup(doc, "right");
        }, "ungroup:delete");
        return true;
      }

      if (node?.parent?.is(UnitNodeModel) && node.parent.inlineNode?.isEmpty) {
        globalShortcuts.Backspace.action(doc, { event, input });
        return true;
      }

      doc.cursor.moveRight();
      doc.selection.resetSelection();
      globalShortcuts.Backspace.action(doc, { event, input });
      return true;
    },
  },
  Backspace: {
    canPickSuggestion: () => false,
    action: (doc: DocumentModel, { event, input }): boolean => {
      const { tiptap } = doc.editor;
      const { node, index, nodeBeforeCursor } = doc.cursor;

      if (input) {
        const { editingNode, inputSelection } = doc.input;
        if (
          !editingNode?.isInlineEditable() ||
          !inputSelection ||
          inputSelection.from > 0 ||
          inputSelection.from !== inputSelection.to
        ) {
          return false;
        }

        if (
          inputSelection.from === 0 &&
          inputSelection.to === 0 &&
          hasUnit(editingNode, "prefix")
        ) {
          const { inlineNode } = editingNode.unitNode;

          doc.editor.resetInput(true);
          doc.cursor.moveTo(inlineNode, inlineNode?.nodes.length);
          triggerKeyboardInput(doc, "Backspace", event);
          return true;
        }
      }

      // Remove the selection if something is selected.
      if (doc.selection.hasSelectedNodes) {
        doc.transact(() => {
          deleteSelectedNodes(doc);
          postProcessNodeRemoval(doc);
        }, "shortcut:Backspace");
        return true;
      }

      if (isMatrixCell(node)) {
        const { parent } = node;
        const { col = 0 } = parent.getCellPosition(node) ?? {};

        if (doc.matrixSelection.isAugmentedBarSelected(parent.id, col - 1)) {
          doc.transact(
            () => {
              parent.removeAugmentedBar(col - 1);
              doc.matrixSelection.resetSelection();
            },
            "matrix:deleteAugmentedBar",
            { runInAction: true },
          );

          return true;
        }

        if (doc.matrixSelection.currentSelection) {
          preserveSelectionCursorPosition(doc, () => {
            deleteMatrixSelection(doc);

            if (canConvertToInline(parent)) {
              replaceMatrixWithCellAtPosition(parent, { row: 0, col: 0 });
            }
          });

          return true;
        }
      }

      if (node?.parent?.is(GraphExpressionNodeModel) && index === 0) {
        if (node.isEmpty) {
          removeGraphExpression(node.parent);
        }

        return true;
      }

      // See the matching branch in `Delete`: breaking the computed link comes before deleting.
      if (nodeBeforeCursor && doc.computedNodes.unlinkComputedNode(nodeBeforeCursor)) {
        return true;
      }

      doc.transact(() => {
        if (hasDecorator(nodeBeforeCursor)) {
          setNodeDecorators(doc, nodeBeforeCursor, []);
        }
        // Reopen a group from the left, remove the opening symbol
        else if (shouldReopenGroupLeft(doc)) {
          reopenGroupLeft(doc);
        }
        // Reopen a group from the right, remove the closing symbol
        else if (shouldReopenGroupRight(doc)) {
          reopenGroupRight(doc);
        }
        // Delete the node in other cases
        else if (shouldUngroup(doc)) {
          ungroup(doc);
        } else if (shouldMoveToOpenGroupLeft(doc) || shouldMoveOutOfOpenGroupRight(doc)) {
          doc.cursor.moveLeft();
          triggerKeyboardInput(doc, "Backspace", event);
        } else if (index === 0 && isMatrixCell(node) && !hasGroupBounds(node.parent.bounds)) {
          selectColumn(node);
        } else if (shouldRemoveSubSupParam(node)) {
          removeSubSupParam(getSubSupOperator(node), event);
        } else if (shouldRemoveLabels(doc, "right")) {
          enterAdjacentLabelAndDelete(doc, "right", event);
        }
        // If this is an operator or function select it first, then delete (except for nrary like add or multiply).
        else if (selectNodeBeforeRemoving(nodeBeforeCursor)) {
          doc.selection.selectNode(nodeBeforeCursor);
          doc.editor.suggestions.reset();
        } else if (
          nodeBeforeCursor?.is(VariableNodeModel) &&
          nodeBeforeCursor.hasParams &&
          nodeBeforeCursor.paramsAreEmpty
        ) {
          const { parameters, power } = nodeBeforeCursor;

          if (!power || parameters.length > 1) {
            doc.cursor.moveLeft();
            triggerKeyboardInput(doc, "Backspace", event);
          } else {
            doc.removeNode(parameters[0]);
            /**
             * Deferred because removing the last param triggers the VariableNodeModel reaction
             * that releases the power back to a sibling — that mutation hasn't happened yet
             * when this code runs, so moveAfterNode must wait for the next microtask.
             */
            queueMicrotask(() => {
              doc.cursor.moveAfterNode(power);
            });
          }
        }
        // If the cursor is at the beginning of the first function parameter, select it first
        else if (index === 0 && node?.parent && selectParentNodeBeforeRemoving(node)) {
          doc.selection.selectNode(node.parent);
        } else if (
          node?.parent?.is(WrapperNodeModel) &&
          index === 0 &&
          !node.isEmpty &&
          nodeIsExpression(tiptap?.state.selection.$from.nodeAfter)
        ) {
          doc.cursor.moveLeft();
        } else if (nodeBeforeCursor && hasUnit(nodeBeforeCursor, "postfix")) {
          doc.cursor.moveLeft();
          triggerKeyboardInput(doc, "Backspace", event);
        } else if (nodeBeforeCursor?.isInlineEditable()) {
          const nodeBefore = nodeBeforeCursor;

          if (nodeBefore.is(NumberNodeModel) && (nodeBefore.subscript || nodeBefore.power)) {
            removeSubSupParam(nodeBefore.subscript || nodeBefore.power, event);
          } else {
            let nodeValue = nodeBefore.value;
            while (nodeValue.length > 0) {
              nodeValue = nodeValue.substring(0, nodeValue.length - 1);
              if (nodeBefore.validateValue(nodeValue)) break;
            }

            if (nodeValue.length > 0) {
              nodeBefore.value = nodeValue;
            } else if (node) {
              if (doc.cursor.isOnNode(node)) {
                deleteNode(node, index);
              }
              doc.selection.resetSelection();
              doc.editor.resetInput(true);
            }
          }
        }
        // Delete the node in other cases
        else if (node) {
          if (doc.cursor.isOnNode(node)) {
            doc.editor.hints.setPhantomHints([]);
            deleteNode(node, index);
          }
          doc.selection.resetSelection();
        }
        postProcessNodeRemoval(doc);
      }, "shortcut:Backspace");
      return true;
    },
  },
  "Mod+Backspace": {
    action: (doc: DocumentModel): boolean => {
      if (!cursorIsActive(doc.cursor)) {
        return false;
      }

      const {
        cursor: { node },
      } = doc;

      if (isMatrixCell(node)) {
        selectRow(node);
        return true;
      }

      doc.transact(() => {
        doc.editor.hints.setPhantomHints([]);
        deleteNode(node, doc.cursor.index);
        doc.selection.resetSelection();
        postProcessNodeRemoval(doc);
      }, "shortcut:Mod+Backspace");
      return true;
    },
  },
  [HOTKEY_MAP["align-left"]]: {
    action: (doc: DocumentModel): boolean => {
      if (!cursorIsActive(doc.cursor) || !doc.editor.tiptap) {
        return false;
      }

      if (!doc.editor.tiptap.commands.setAlign("left")) {
        return false;
      }

      requestAnimationFrame(() => {
        doc.input.focus();
      });

      return true;
    },
  },
  [HOTKEY_MAP["align-center"]]: {
    action: (doc: DocumentModel): boolean => {
      if (!cursorIsActive(doc.cursor) || !doc.editor.tiptap) {
        return false;
      }

      if (!doc.editor.tiptap.commands.setAlign("center")) {
        return false;
      }

      requestAnimationFrame(() => {
        doc.input.focus();
      });

      return true;
    },
  },
  [HOTKEY_MAP["align-right"]]: {
    action: (doc: DocumentModel): boolean => {
      if (!cursorIsActive(doc.cursor) || !doc.editor.tiptap) {
        return false;
      }

      if (!doc.editor.tiptap.commands.setAlign("right")) {
        return false;
      }

      requestAnimationFrame(() => {
        doc.input.focus();
      });

      return true;
    },
  },
} satisfies Partial<Record<KeyCombination | Symbol, KeyboardInputAction>>;
